import { Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, ArrowRight } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { PageHeader } from "@/components/site/PageHeader";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { IMAGES, SERVICE_REGIONS } from "@/data/content";

export default function ServiceAreas() {
  useSeo({
    title: "Service Areas | HVAC Contractor Serving All of Massachusetts",
    description:
      "Lighthouse Home Services proudly serves homeowners throughout Massachusetts — Worcester County, Greater Boston, MetroWest, Cape Cod and the Pioneer Valley.",
  });

  return (
    <div>
      <PageHeader
        eyebrow="Service Areas"
        title={<>Proudly serving Massachusetts.</>}
        intro="From the Worcester hills to the Cape Cod coast, we bring dependable comfort and honest service to homeowners across the Commonwealth."
        image={IMAGES.coastalHome}
      />

      <section className="mx-auto max-w-7xl px-5 md:px-8 py-16 md:py-24">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <Reveal>
              <div className="overflow-hidden rounded-3xl border border-border shadow-[0_8px_30px_rgb(11,29,58,0.06)]" data-testid="service-map">
                <MapContainer
                  center={[42.15, -71.6]}
                  zoom={8}
                  scrollWheelZoom={false}
                  style={{ height: "34rem", width: "100%" }}
                >
                  <TileLayer
                    attribution='&copy; OpenStreetMap &copy; CARTO'
                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                  />
                  {SERVICE_REGIONS.map((r) => (
                    <CircleMarker
                      key={r.name}
                      center={r.center}
                      radius={16}
                      pathOptions={{ color: "#0B1D3A", weight: 2, fillColor: "#D4AF37", fillOpacity: 0.55 }}
                    >
                      <Tooltip direction="top" offset={[0, -8]} opacity={1} className="lh-tooltip">
                        <span>{r.name}</span>
                      </Tooltip>
                    </CircleMarker>
                  ))}
                </MapContainer>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-4">
            <Reveal delay={0.1}><Eyebrow>Regions We Cover</Eyebrow></Reveal>
            <div className="mt-6 space-y-3">
              {SERVICE_REGIONS.map((r, i) => (
                <Reveal key={r.name} delay={0.05 * i}>
                  <div className="rounded-2xl bg-sand p-5">
                    <h3 className="flex items-center gap-2 font-serif text-xl text-navy">
                      <MapPin className="h-4 w-4 text-gold" /> {r.name}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">{r.homes}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 md:px-8 pb-24 md:pb-32 text-center">
        <Reveal>
          <h2 className="font-serif text-4xl md:text-6xl leading-tight text-navy">Not sure if you're in our area?</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-muted-foreground max-w-xl mx-auto">Reach out — chances are we serve your town. We're always happy to help a fellow Massachusetts homeowner.</p>
        </Reveal>
        <Reveal delay={0.15}>
          <Link to="/contact" className="mt-9 inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-navy/85">
            Request a Quote <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
